import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import mealApi from "../../../api/meal/mealApi";
import type {
  MealPickUpData,
  MealPickUpPayloadUnion,
} from "../../../api/meal/types/mealPickUpType";
import type {
  MealTodayData,
  MealTodayPayload,
} from "../../../api/meal/types/mealTodayType";
import {
  isRequestCancelled,
  type RejectPayload,
  toRejectPayload,
} from "../../../helpers/ApiError";
import type { RootState } from "../../store";

interface MealState {
  today: MealTodayData | null;
  isLoadingToday: boolean;
  todayError: string | null;
  todayRequestId: string | null;
  isSubmittingPickup: boolean;
  pickupError: string | null;
  pickupRequestId: string | null;
  lastPickupResult: MealPickUpData | null;
}

const initialState: MealState = {
  today: null,
  isLoadingToday: false,
  todayError: null,
  todayRequestId: null,
  isSubmittingPickup: false,
  pickupError: null,
  pickupRequestId: null,
  lastPickupResult: null,
};

export const fetchMealToday = createAsyncThunk<
  MealTodayData,
  MealTodayPayload,
  { rejectValue: RejectPayload }
>("meal/fetchMealToday", async (payload, { rejectWithValue, signal }) => {
  try {
    const res = await mealApi.getMealToday(payload, signal);
    return res.data;
  } catch (err) {
    if (isRequestCancelled(err, signal)) throw err;

    return rejectWithValue(
      toRejectPayload(err, "Gagal memuat data meal hari ini"),
    );
  }
});

export const submitMealPickup = createAsyncThunk<
  MealPickUpData,
  MealPickUpPayloadUnion,
  { rejectValue: RejectPayload }
>("meal/submitMealPickup", async (payload, { rejectWithValue, signal }) => {
  try {
    const res = await mealApi.createMealPickup(payload, signal);
    return res.data;
  } catch (err) {
    if (isRequestCancelled(err, signal)) throw err;

    return rejectWithValue(
      toRejectPayload(err, "Gagal menyimpan pilihan meal"),
    );
  }
});

const mealSlice = createSlice({
  name: "meal",
  initialState,
  reducers: {
    resetMealToday: () => initialState,
    clearMealPickupError: (state) => {
      state.pickupError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMealToday.pending, (state, action) => {
        state.today = null;
        state.isLoadingToday = true;
        state.todayError = null;
        state.todayRequestId = action.meta.requestId;
      })
      .addCase(fetchMealToday.fulfilled, (state, action) => {
        if (state.todayRequestId !== action.meta.requestId) return;

        state.isLoadingToday = false;
        state.today = action.payload;
        state.todayError = null;
        state.todayRequestId = null;
      })
      .addCase(fetchMealToday.rejected, (state, action) => {
        if (state.todayRequestId !== action.meta.requestId) return;

        state.isLoadingToday = false;
        state.todayRequestId = null;

        if (action.meta.aborted) {
          state.todayError = null;
          return;
        }

        state.today = null;
        state.todayError = action.payload?.message ?? "Gagal memuat data";
      })
      .addCase(submitMealPickup.pending, (state, action) => {
        state.isSubmittingPickup = true;
        state.pickupError = null;
        state.lastPickupResult = null;
        state.pickupRequestId = action.meta.requestId;
      })
      .addCase(submitMealPickup.fulfilled, (state, action) => {
        if (state.pickupRequestId !== action.meta.requestId) return;

        state.isSubmittingPickup = false;
        state.pickupError = null;
        state.pickupRequestId = null;
        state.lastPickupResult = action.payload;

        if (state.today) {
          state.today.can_pick_menu = false;
          state.today.selection = {
            is_selected: true,
            meal_item_id: action.payload.id,
            type: action.payload.type,
            status: action.payload.status,
            reason: action.payload.reason,
            menu: action.payload.menu,
          };
        }
      })
      .addCase(submitMealPickup.rejected, (state, action) => {
        if (state.pickupRequestId !== action.meta.requestId) return;

        state.isSubmittingPickup = false;
        state.pickupRequestId = null;

        if (action.meta.aborted) {
          state.pickupError = null;
          return;
        }

        state.pickupError =
          action.payload?.message ?? "Gagal menyimpan pilihan";
      });
  },
});

export const { resetMealToday, clearMealPickupError } = mealSlice.actions;

export const selectMealToday = (state: RootState) => state.mealReducer.today;
export const selectMealTodayLoading = (state: RootState) =>
  state.mealReducer.isLoadingToday;
export const selectMealTodayError = (state: RootState) =>
  state.mealReducer.todayError;
export const selectIsSubmittingPickup = (state: RootState) =>
  state.mealReducer.isSubmittingPickup;
export const selectPickupError = (state: RootState) =>
  state.mealReducer.pickupError;
export const selectLastPickupResult = (state: RootState) =>
  state.mealReducer.lastPickupResult;

export default mealSlice.reducer;
